import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    SchoolAutoReplaceSetting,
    SchoolInformation,
    SchoolUser,
    TeachingEnvSupport,
} from '../firebase/types-school'
import { LatLng } from 'leaflet'
import { RootState } from './store'
import { DEFAULTS } from '../firebase/defaults'

export type SchoolConfig =
    | {
          loaded: false
          data: {
              country: undefined
              locationState: undefined
              schoolId: undefined
          }
      }
    | {
          loaded: true
          data: {
              country: string
              locationState: string
              schoolId: string
          }
      }

interface ISchoolSlice {
    // schoolId?: string | null
    school: SchoolInformation

    information:
        | {
              loading: true
              data: undefined
          }
        | {
              loading: false
              data: SchoolInformation | null
          }

    config: SchoolConfig
    schoolLoaded: boolean
    schoolUser:
        | {
              loading: true
              data: undefined
          }
        | {
              loading: false
              data: SchoolUser | null
          }
    adminCount?: number

    parkingLocation?: LatLng
}

export const initialState: ISchoolSlice = {
    schoolLoaded: false,
    school: {},
    information: {
        loading: true,
        data: undefined,
    },
    schoolUser: {
        loading: true,
        data: undefined,
    },
    config: {
        loaded: false,
        data: {
            country: undefined,
            locationState: undefined,
            schoolId: undefined,
        },
    },
}

export const schoolSlice = createSlice({
    name: 'school',
    initialState,
    reducers: {
        setSchoolDefault: () => {
            // state.school = initialState.school
            // state.schoolLoaded = false
            // state.config = initialState.config
            // state.schoolUser = initialState.schoolUser
            // state.schoolId = initialState.schoolId
            return initialState
        },
        clearSchoolData: (state) => {
            state.school = initialState.school
            state.information = initialState.information
            state.schoolUser = initialState.schoolUser
            // state.schoolId = undefined
            state.config = initialState.config
        },
        // setSelectedSchool: (
        //     state,
        //     action: PayloadAction<string | undefined | null>
        // ) => {
        //     state.schoolId = action.payload
        // },
        setSchool: (state, action: PayloadAction<SchoolInformation>) => {
            Object.assign(state.school, action.payload)
            state.schoolLoaded = true

            if (
                action.payload.location?.country &&
                action.payload.location?.locationState &&
                action.payload.id
            ) {
                state.config.loaded = true
                if (state.config.loaded === true) {
                    Object.assign(state.config.data, {
                        country: action.payload.location?.country,
                        locationState: action.payload.location?.locationState,
                        schoolId: action.payload.id,
                    })
                }
            } else {
                state.config = initialState.config
            }
        },
        setSchool_v2: (
            state,
            action: PayloadAction<SchoolInformation | null>
        ) => {
            Object.assign(state.information, {
                loading: false,
                data: action.payload,
            })

            if (
                action.payload?.location?.country &&
                action.payload.location?.locationState &&
                action.payload.id
            ) {
                state.config.loaded = true
                if (state.config.loaded === true) {
                    Object.assign(state.config.data, {
                        country: action.payload.location?.country,
                        locationState: action.payload.location?.locationState,
                        schoolId: action.payload.id,
                    })
                }
            } else {
                state.config = initialState.config
            }
        },
        setSchoolUser: (state, action: PayloadAction<SchoolUser | null>) => {
            Object.assign(state.schoolUser, {
                loading: false,
                data: action.payload,
            })
        },
        setAdminCount: (state, action: PayloadAction<number>) => {
            state.adminCount = action.payload
        },
    },
})

// School User helpers
export const selectSchoolUser = (state: RootState) => state.school.schoolUser
export const selectSchoolUserData = (state: RootState) =>
    state.school.schoolUser.data
export const selectSchoolUserState = (state: RootState) =>
    !state.school.schoolUser.loading
        ? state.school.schoolUser.data?.school?.accountState ||
          DEFAULTS.school.userAccountState
        : undefined
export const selectSchoolUserAuthority = (state: RootState) =>
    !state.school.schoolUser.loading
        ? state.school.schoolUser.data?.school?.authority ||
          DEFAULTS.school.userAuthority
        : undefined
export const selectSchoolUserNotificationSettings = (state: RootState) =>
    !state.school.schoolUser.loading
        ? state.school.schoolUser.data?.notificationSettings || null
        : undefined

// School Information helpers
export const selectSchool = (state: RootState) =>
    state.school.information.loading ? undefined : state.school.information.data

export const selectSchoolState = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.account?.state ||
          DEFAULTS.school.accountState
export const selectSchoolPolicies = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.policies || null

export const makeSelectBellTimesByType = () =>
    createSelector(
        [selectSchool, (_, type: 'Primary' | 'Secondary') => type],
        (schoolInformation, type) => {
            if (schoolInformation === undefined) return undefined

            let result
            switch (type) {
                case 'Primary':
                    result = schoolInformation?.primaryTimes
                    break
                case 'Secondary':
                    result = schoolInformation?.secondaryTimes
                    break
            }
            return result || schoolInformation?.times || null
        }
    )
export const selectSchoolPrimaryBellTimes = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : !!state.school.information.data?.primaryTimes
          ? {
                name: 'primaryTimes',
                data: state.school.information.data.primaryTimes,
            }
          : !!state.school.information.data?.times
            ? {
                  key: 'times',
                  data: state.school.information.data?.times,
              }
            : null
export const selectSchoolSecondaryBellTimes = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : !!state.school.information.data?.secondaryTimes
          ? {
                name: 'secondaryTimes',
                data: state.school.information.data.secondaryTimes,
            }
          : !!state.school.information.data?.times
            ? {
                  key: 'times',
                  data: state.school.information.data?.times,
              }
            : null

export const selectSchoolDetails = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details || null
export const selectSchoolType = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details?.schoolType || null
export const selectSchoolName = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details?.schoolName || null
export const selectSchoolContact = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details?.contactNumber || ''
export const selectSchoolContactType = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details?.contactType || null
export const selectSchoolLogo = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.logo?.value || null

export const selectSchoolBM = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.businessManager || null
export const selectSchoolBMEmail = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.businessManager?.invoiceEmail || null
export const selectSchoolActiveAutoreplace = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.details?.activeAutoReplacements || null

export const makeSelectSchoolActiveAutoreplaceForType = () =>
    createSelector(
        [
            selectSchoolActiveAutoreplace,
            (_, keyName: keyof SchoolAutoReplaceSetting) => keyName,
        ],
        (settings, keyName) =>
            settings === undefined ? undefined : !!settings?.[keyName]
    )

export const selectSchoolFavourites = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.favourites || null

export const makeSelectIsTeacherFavouritedBySchool = () =>
    createSelector(
        [selectSchoolFavourites, (_, teacherUid: string) => teacherUid],
        (favourites, teacherUid) =>
            favourites === undefined ? undefined : !!favourites?.[teacherUid]
    )

export const selectSchoolParking = (state: RootState) =>
    state.school.school.parking
export const selectSchoolLocation = (state: RootState) =>
    state.school.school.location
export const selectSchoolMultipleLocations = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.multipleLocations || null
export const selectSchoolCoordinates = (state: RootState): [number, number] => [
    state.school.school.location?.lat || 0,
    state.school.school.location?.long || 0,
]

export const selectSchoolProfile = (state: RootState) =>
    state.school.information.loading
        ? undefined
        : state.school.information.data?.profile?.aboutSchool || ''

export const selectSchoolConfig = (state: RootState) => state.school.config
export const selectSchoolUid = (state: RootState) =>
    state.school.config.data.schoolId

export const makeSelectSchoolTeachingEnvSupport = () =>
    createSelector(
        [
            (state: RootState) =>
                state.school.school.profile?.teachingEnvSupport,
            (_: RootState, name: keyof TeachingEnvSupport) => name,
        ],
        (profile, name) => profile?.[name]
    )

export const {
    setSchoolDefault,
    clearSchoolData,
    // setSelectedSchool,
    setSchool,
    setSchool_v2,
    setSchoolUser,
    setAdminCount,
} = schoolSlice.actions

export default schoolSlice.reducer
